import { useState } from "react";
import { Box, Alert } from "@mui/material";
import type {
  ReminderWithUser,
  ShipmentOverviewInfo,
} from "../../../src/shipments/reminders/types";
import { ShipmentReminderComponent } from "./ShipmentReminderComponent";

interface ReminderWithOptionalShipmentOverview extends ReminderWithUser {
  shipmentOverview?: ShipmentOverviewInfo | null;
}

interface ShipmentTimelineRemindersComponentProps {
  shipmentId?: string;
  reminders: ReminderWithOptionalShipmentOverview[];
  showShipmentOverviewInfo?: boolean;
  onEmailOpen: (id: string) => void;
  onShipmentClick?: (shipmentId: string) => void;
}

export function ShipmentTimelineRemindersComponent({
  reminders: initialReminders,
  showShipmentOverviewInfo = false,
  onShipmentClick,
  onEmailOpen,
}: ShipmentTimelineRemindersComponentProps) {
  const [reminders, setReminders] =
    useState<ReminderWithOptionalShipmentOverview[]>(initialReminders);
  const [error, setError] = useState<string | null>(null);

  // Filter and sort all reminders by date (most recent first)
  const sortedReminders = reminders
    .filter((r) => r.status !== "resolved") // Only show unresolved
    .sort((a, b) => {
      const timeA = a.triggerTime ? new Date(a.triggerTime).getTime() : 0;
      const timeB = b.triggerTime ? new Date(b.triggerTime).getTime() : 0;
      return timeB - timeA; // Most recent first
    });

  const handleReminderUpdated = (reminderId: string, resolved: boolean) => {
    // Update the reminder in the list
    setReminders((prev) =>
      prev.map((r) => {
        if (r.id === reminderId) {
          return {
            ...r,
            status: resolved ? "resolved" : "pending",
            resolvedTime: resolved ? new Date() : null,
          };
        }
        return r;
      })
    );
  };

  if (sortedReminders.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* All Reminders - Sorted by Date */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {sortedReminders.map((reminder) => (
          <ShipmentReminderComponent
            key={reminder.id}
            reminder={reminder}
            showShipmentOverviewInfo={showShipmentOverviewInfo}
            onEmailOpen={onEmailOpen}
            onShipmentClick={onShipmentClick}
            onReminderUpdated={handleReminderUpdated}
          />
        ))}
      </Box>
    </Box>
  );
}
